import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import './index.css'
import App from './App.tsx'
import { BrowserRouter } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'

// Frontend error tracking (P2-12).
//
// The DSN is a public identifier (it ships in every browser bundle by design),
// so a production-build default is safe here; VITE_SENTRY_DSN still overrides
// it. Dev builds stay silent unless the env var is set explicitly.
const DEFAULT_PROD_DSN =
  'https://67f9b0eb8c6e05716c7064e2d198c681@o4511704340627456.ingest.us.sentry.io/4511704346656768'
const sentryDsn =
  import.meta.env.VITE_SENTRY_DSN || (import.meta.env.PROD ? DEFAULT_PROD_DSN : undefined)

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing: sample 10% — enough to see slow flows without burning the free
    // quota (Sentry's onboarding default of 1.0 captures EVERY pageload).
    tracesSampleRate: 0.1,
    // Propagate trace headers only to our own API.
    tracePropagationTargets: ['localhost', /^https:\/\/api\.defellix\.com/],
    // Session Replay: record nothing routinely; capture the full session
    // whenever an error occurs (the replays that actually matter).
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Don't send events for benign network blips or third-party in-app browser (Instagram/Meta) injected script crashes.
    ignoreErrors: [
      // Network blips
      'Network Error',
      'Request aborted',
      'timeout exceeded',
      'Failed to fetch',
      'Load failed',
      // Instagram / Meta In-App Browser (IAB) injected script errors
      'Error invoking postMessage: Java object is gone',
      /Error invoking postMessage/i,
      /window\.webkit\.messageHandlers/i,
      /sendDataToNative/i,
      /sendBeforeUnloadMessage/i,
      /sendPageHideMessage/i,
      // Benign observer messages
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
    denyUrls: [
      // Instagram / Meta Android & iOS In-App Browser injected scripts
      /^iabjs:\/\//i,
      /navigation_performance_logger/i,
    ],
    beforeSend(event) {
      const errorMessage = event.exception?.values?.[0]?.value || ''
      const stackFrames = event.exception?.values?.[0]?.stacktrace?.frames || []

      // Filter out Meta/Instagram IAB pagehide/beforeunload errors
      if (
        errorMessage.includes('Java object is gone') ||
        errorMessage.includes('window.webkit.messageHandlers')
      ) {
        return null
      }

      // Filter out stack traces originating from IAB injected scripts
      const isIabError = stackFrames.some(
        (frame) =>
          (frame.filename && (frame.filename.includes('iabjs://') || frame.filename.includes('navigation_performance_logger'))) ||
          (frame.function && (frame.function.includes('sendDataToNative') || frame.function.includes('sendPageHideMessage') || frame.function.includes('sendBeforeUnloadMessage')))
      )

      if (isIabError) {
        return null
      }

      return event
    },
  })

  // Manual smoke-test hook: run `__sentryTest()` in the browser console and the
  // event should appear in the Sentry dashboard within seconds.
  ;(window as unknown as Record<string, unknown>).__sentryTest = () => {
    Sentry.captureException(new Error('Sentry wiring test — everything works'))
    return 'sent — check the Sentry dashboard'
  }
}

const posthogKey = import.meta.env.VITE_POSTHOG_KEY
const posthogHost = import.meta.env.VITE_POSTHOG_HOST

if (posthogKey && posthogHost) {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    defaults: '2026-05-30',
    capture_pageview: 'history_change',
  })
} else if (import.meta.env.DEV) {
  console.error('VITE_POSTHOG_KEY or VITE_POSTHOG_HOST variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once VITE_POSTHOG_KEY and VITE_POSTHOG_HOST are configured')
}

createRoot(document.getElementById('root')!).render(
  <BrowserRouter>
    <AuthProvider>
      <App />
    </AuthProvider>
  </BrowserRouter>
)
